'use client';

import React, { useMemo } from 'react';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { useProfile } from 'src/context/providers/ProfileProvider';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { useGetProviderVolumeQuery } from 'src/api/redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { claimsOrEncounters, applyDisplayMode } from 'src/components/Profiles/shared/widgets/utils';
import { getBenchmarkState, HERO_COLOR } from 'src/components/Profiles/shared/widgets/shared/benchmark';

const VolumeDifferenceContent: React.FC = () => {
  const widgetConfig = useWidgetConfig();
  const widget = widgetConfig?.widget ?? null;
  const dateRangeLabel = widgetConfig?.dateRangeLabel ?? '';
  const { entityDetails } = useProfile();
  const entityId = entityDetails?.id;
  const { showEncountersTotals } = useFlags();

  const minuendCodeSetId = widget?.minuend_code_set?.id;
  const minuendEncounterId = widget?.minuend_encounter?.id;
  const subtrahendCodeSetId = widget?.subtrahend_code_set?.id;
  const subtrahendEncounterId = widget?.subtrahend_encounter?.id;

  const hasMinuendId = !!(minuendCodeSetId || minuendEncounterId);
  const hasSubtrahendId = !!(subtrahendCodeSetId || subtrahendEncounterId);

  const minYear = widgetConfig?.minYear;
  const maxYear = widgetConfig?.maxYear;
  const dateRange =
    widget?.min_year != null || widget?.max_year != null
      ? { gte: String(minYear), lte: String(maxYear) }
      : undefined;

  const { data: minuendData, isLoading: minuendLoading } =
    useGetProviderVolumeQuery(
      {
        providerId: entityId || '',
        filter_set: minuendCodeSetId ?? undefined,
        cohort_id: minuendEncounterId ?? undefined,
        dateRange,
      },
      { skip: !entityId || !hasMinuendId }
    );

  const { data: subtrahendData, isLoading: subtrahendLoading } =
    useGetProviderVolumeQuery(
      {
        providerId: entityId || '',
        filter_set: subtrahendCodeSetId ?? undefined,
        cohort_id: subtrahendEncounterId ?? undefined,
        dateRange,
      },
      { skip: !entityId || !hasSubtrahendId }
    );

  const isLoading = minuendLoading || subtrahendLoading;

  const minuendLabel =
    widget?.minuend_code_set?.label ??
    widget?.minuend_encounter?.label ??
    'Minuend';
  const subtrahendLabel =
    widget?.subtrahend_code_set?.label ??
    widget?.subtrahend_encounter?.label ??
    'Subtrahend';

  const benchmark = widget?.benchmark ?? null;
  const benchmarkOperator = widget?.benchmark_operator ?? 'greater_than';
  const displayMode = widget?.display_mode;

  const volumeType = widget?.volume_type ?? 'patients';

  const differenceData = useMemo(() => {
    if (!minuendData || !subtrahendData) return null;

    const minuendVolume =
      volumeType === 'claims'
        ? Number(
            claimsOrEncounters(
              minuendData,
              !!minuendEncounterId,
              showEncountersTotals
            ) ?? 0
          )
        : Number(minuendData[volumeType] ?? 0);
    const subtrahendVolume =
      volumeType === 'claims'
        ? Number(
            claimsOrEncounters(
              subtrahendData,
              !!subtrahendEncounterId,
              showEncountersTotals
            ) ?? 0
          )
        : Number(subtrahendData[volumeType] ?? 0);

    const rawDifference = minuendVolume - subtrahendVolume;
    const difference = applyDisplayMode(rawDifference, displayMode);

    return { difference, minuendVolume, subtrahendVolume };
  }, [
    minuendData,
    subtrahendData,
    volumeType,
    minuendEncounterId,
    subtrahendEncounterId,
    showEncountersTotals,
    displayMode,
  ]);

  const hasData =
    differenceData?.difference !== null &&
    differenceData?.difference !== undefined;

  const benchmarkState =
    hasData && benchmark !== null
      ? getBenchmarkState(differenceData!.difference, benchmark, benchmarkOperator)
      : null;

  const heroColor = benchmarkState
    ? HERO_COLOR[benchmarkState]
    : 'text-sky-600';

  if (isLoading) {
    return <WidgetLoading data-testid="compact-volume-difference-loading" aria-label="Loading volume difference data" />;
  }

  if (!hasData) {
    return <WidgetEmpty data-testid="compact-volume-difference-empty" />;
  }

  return (
    <div
      className="flex flex-col gap-3"
      data-testid="compact-volume-difference-content"
    >
      {/* Hero difference */}
      <div
        className="flex items-center gap-3 min-w-0"
        data-testid="compact-volume-difference-hero-section"
      >
        <span
          className={`text-5xl font-bold tabular-nums shrink-0 ${heroColor}`}
          data-testid="compact-volume-difference-hero"
          data-benchmark-state={benchmarkState ?? 'none'}
          aria-label={`Difference: ${differenceData!.difference.toLocaleString()}${
            benchmarkState ? `, ${benchmarkState} benchmark` : ''
          }`}
        >
          {differenceData!.difference.toLocaleString()}
        </span>
        <div
          className="flex flex-col min-w-0"
          data-testid="compact-volume-difference-label"
        >
          <span className="text-xs text-neutral-900 font-bold">
            {minuendLabel}
          </span>
          <span className="text-xs text-neutral-900">{' minus '}</span>
          <span className="text-xs text-neutral-900 font-bold">
            {subtrahendLabel}
          </span>
          <span
            className="text-xs text-neutral-400"
            data-testid="compact-volume-difference-date-range"
          >
            {displayMode === 'monthly_average'
              ? 'per month from '
              : 'per year from '}
            {dateRangeLabel}
          </span>
        </div>
      </div>

      {/* Data Summary */}
      <div data-testid="compact-volume-difference-data-summary">
        <div className="border-t border-neutral-200" />
        <div
          className="flex flex-col gap-2 py-3 text-sm text-neutral-600"
          data-testid="compact-volume-difference-data-summary-content"
        >
          <div className="flex items-center justify-between gap-2">
            <span>{minuendLabel}</span>
            <span className="font-semibold tabular-nums shrink-0">
              {differenceData!.minuendVolume.toLocaleString()}
            </span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span>{subtrahendLabel}</span>
            <span className="font-semibold tabular-nums shrink-0">
              {differenceData!.subtrahendVolume.toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VolumeDifferenceContent;
